<script setup lang="ts">
/**
 * Three things that keep an account hard to take and easy to get back, with a progress bar in the same voice as the
 * tier bar above it. Every row links to Sign-in & Security. It stays visible at three of three: that is the badge.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add AccountProtectionChecklist.vue.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import { computed } from 'vue';
import type { User } from '@/defs/user';
import useAppStore from '@/store';

interface ChecklistItem {
  label: string;
  detail: string;
  done: boolean;
}

const appStore = useAppStore();
const user = computed<User | null>(() => appStore.state.user as User | null);

const items = computed<ChecklistItem[]>(() => {
  const recovery = user.value?.recovery_codes_remaining ?? 0;

  return [
    {
      label: 'Passkey or authenticator app',
      detail: user.value?.has_strong_two_factor ? 'On' : 'Sign in with something you hold, not a code sent to you',
      done: true === user.value?.has_strong_two_factor,
    },
    {
      label: 'Recovery codes',
      detail: recovery ? `${recovery} unused` : 'Your way back in if you lose a device',
      done: recovery > 0,
    },
    {
      label: 'Mobile number verified',
      detail: user.value?.sms_number_verified_at ? 'On' : 'A second way to receive a code',
      done: !!user.value?.sms_number_verified_at,
    },
  ];
});

const doneCount = computed<number>(() => items.value.filter((item) => item.done).length);
const percent = computed<number>(() => Math.round((doneCount.value / items.value.length) * 100));
</script>

<template>
  <template v-if="user">
    <h2>Account Protection</h2>
    <peppermill-content-block>
      <div class="protection--bar" :style="{ '--percent-complete': percent + '%' }">
        <div class="protection--bar_text">
          {{ doneCount === items.length ? 'Account protected' : `${doneCount} of ${items.length} complete` }}
        </div>
        <div class="protection--bar_fill"></div>
      </div>
      <ul class="protection--list">
        <li v-for="item in items" :key="item.label">
          <router-link :to="{ name: 'account.security' }" class="protection--row" :class="{ 'protection--row-done': item.done }">
            <span class="material-icons" aria-hidden="true">{{ item.done ? 'check_circle' : 'radio_button_unchecked' }}</span>
            <span>
              <strong>{{ item.label }}</strong>
              <small>{{ item.detail }}</small>
            </span>
          </router-link>
        </li>
      </ul>
    </peppermill-content-block>
  </template>
</template>

<style scoped>
.protection--bar {
  --percent-complete: 0%;
  position: relative;
  height: 35px;
  margin: 0 0 1em;
  background: rgba(255, 255, 255, 0.85);
  overflow: hidden;
}

.protection--bar_text {
  position: absolute;
  inset: 0;
  line-height: 35px;
  text-align: center;
  z-index: 10;
  font-size: 1.1em;
}

.protection--bar_fill {
  position: absolute;
  top: 0;
  left: 0;
  width: var(--percent-complete);
  height: 100%;
  background: rgb(var(--status-success, 46, 125, 50));
  opacity: 0.35;
  transition: width 1250ms ease-in-out;
}

.protection--list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.protection--row {
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 0.5em 0;
  color: inherit;
  text-decoration: none;
}

.protection--row small {
  display: block;
  opacity: 0.8;
}

.protection--row .material-icons {
  color: rgba(0, 0, 0, 0.38);
}

.protection--row-done .material-icons {
  color: rgb(var(--status-success, 46, 125, 50));
}
</style>
